import type { AgentOrigin } from '@blocksuite/affine/store';
import {
  type AgentDocUpdate,
  ClientActionAgentSchema,
  type ClientActionRef,
} from '@streamlain/copilot-realtime-contract';
import type {
  PublicationRefusal,
  RefusedPublicationResolution,
  RefusedPublicationStorage,
} from '@streamlain/doc-publication-contract';
import {
  type DocPublicationStorage,
  type LocalPublication,
  type LocalPublicationReceipt,
  PublicationError,
} from '@streamlain/doc-publication-contract';
import { isEmptyUpdate } from '@streamlain/doc-publication-contract';
import { groupBy } from 'lodash-es';
import { nanoid } from 'nanoid';
import {
  combineLatest,
  filter,
  first,
  lastValueFrom,
  map,
  Observable,
  ReplaySubject,
  share,
  skip,
  Subject,
  throttleTime,
} from 'rxjs';
import {
  applyUpdate,
  type Doc as YDoc,
  encodeStateAsUpdate,
  encodeStateVectorFromUpdate,
  Map as YMap,
  mergeUpdates,
  type Transaction as YTransaction,
} from 'yjs';

import type { DocRecord, DocStorage } from '../storage';
import { isRefusedPublicationStorage } from '../storage/refused-publications';
import type {
  DocSync,
  DocSyncRequestSource,
  IsolatedDocFailure,
  WarmCacheSelection,
} from '../sync/doc';
import { provenanceRuns } from '../sync/doc/provenance-runs';
import { AsyncPriorityQueue } from '../utils/async-priority-queue';
import { isBuildDebugEnabled } from '../utils/build-config';
import { addCumulativePriority } from '../utils/cumulative-priority';
import { remoteDiffChangesLocal } from '../utils/doc-updates';
import { traceImportAwait } from '../utils/import-seam-diagnostics';
import { takeUntilAbort } from '../utils/take-until-abort';
import { MANUALLY_STOP, throwIfAborted } from '../utils/throw-if-aborted';
import { createAgentOrigin } from './agent-origin';

function isPublicationWriterStorage(
  storage: DocStorage
): storage is DocStorage &
  Pick<
    DocPublicationStorage,
    'readPublicationBase' | 'commitPublication' | 'listPendingPublications'
  > {
  return (
    'readPublicationBase' in storage &&
    typeof storage.readPublicationBase === 'function' &&
    'commitPublication' in storage &&
    typeof storage.commitPublication === 'function' &&
    'listPendingPublications' in storage &&
    typeof storage.listPendingPublications === 'function'
  );
}

const NBSTORE_ORIGIN = 'nbstore-frontend';

type Job =
  | {
      type: 'load';
      docId: string;
    }
  | {
      type: 'save';
      docId: string;
      update: Uint8Array;
      clientAction?: ClientActionRef;
    }
  | {
      type: 'apply';
      docId: string;
      update: Uint8Array;
      agent?: AgentDocUpdate;
    };

interface DocFrontendOptions {
  mergeUpdates?: (updates: Uint8Array[]) => Promise<Uint8Array> | Uint8Array;
  /**
   * Callback when Y.js data corruption is detected during applyUpdate.
   * Use this to report corruption to monitoring services.
   */
  onCorruptionDetected?: (info: {
    docId: string;
    error: Error;
    binaryData: Uint8Array;
    operation: 'applyUpdate';
  }) => void;
}

export function isDocSyncUnavailable(
  state:
    | {
        readonly syncRetrying: boolean;
        readonly syncErrorMessage: string | null;
      }
    | null
    | undefined
): boolean {
  return !!state?.syncRetrying && !!state.syncErrorMessage;
}

export type DocFrontendDocState = {
  /**
   * some data is available in yjs doc instance
   */
  ready: boolean;
  /**
   * data is loaded from local doc storage and applied to yjs doc instance
   */
  loaded: boolean;
  /**
   * some data is being applied to yjs doc instance, or some data is being saved to local doc storage
   */
  updating: boolean;
  /**
   * the doc is syncing with remote peers
   */
  syncing: boolean;
  /**
   * the doc is synced with remote peers
   */
  synced: boolean;
  /**
   * the doc is retrying to sync with remote peers
   */
  syncRetrying: boolean;
  /**
   * the error message when syncing with remote peers
   */
  syncErrorMessage: string | null;
  /**
   * isolated per-doc sync failure, if this doc is deferred without a global retry
   */
  syncIsolatedFailure: IsolatedDocFailure | null;
  /**
   * a remote peer announced a newer server state than this device has pulled
   */
  syncRemoteAhead: boolean;
};

export type DocFrontendState = {
  /**
   * total number of docs
   */
  total: number;
  /**
   * number of docs that have been loaded to yjs doc instance
   */
  loaded: number;
  /**
   * some data is being applied to yjs doc instance, or some data is being saved to local doc storage
   */
  updating: boolean;
  /**
   * number of docs that are syncing with remote peers
   */
  syncing: number;
  /**
   * whether all docs are synced with remote peers
   */
  synced: boolean;
  /**
   * whether the doc is retrying to sync with remote peers
   */
  syncRetrying: boolean;
  /**
   * the error message when syncing with remote peers
   */
  syncErrorMessage: string | null;
  /**
   * isolated per-doc sync failures that do not represent global connectivity
   */
  syncIsolatedFailures: IsolatedDocFailure[];
};

/**
 * The remote end of a local publication. `done`: it left the journal, stored
 * by the server or discarded by the user. `refused`: the server refused it
 * for good; it and every other publication of its documents were discarded,
 * the server state of `docIds` pulled, and their open copies detached, so
 * the writer reopens them and applies its change again.
 */
export type PublicationSettlement =
  | { readonly state: 'done' }
  | {
      readonly state: 'refused';
      readonly refusal: PublicationRefusal;
      /** Documents discarded and pulled now; empty when an earlier refusal did it. */
      readonly docIds: readonly string[];
    };

/** First wait before a failed load of an open page is tried again. */
export const LOAD_RETRY_BASE_MS = 1_000;
/** Longest wait between two tries of a failed load. */
export const LOAD_RETRY_MAX_MS = 60_000;

export class DocFrontend implements RefusedPublicationStorage {
  private readonly managedDatabaseDocs = new Set<string>();
  private readonly pendingPublications = new Map<
    string,
    Set<Promise<unknown>>
  >();

  private get publicationStorage(): Pick<
    DocPublicationStorage,
    'readPublicationBase' | 'commitPublication' | 'listPendingPublications'
  > {
    const storage = this.storage;
    if (!isPublicationWriterStorage(storage))
      throw new PublicationError('FORMAT_MISMATCH');
    return storage;
  }

  async readPublicationBase(docIds: readonly string[], signal?: AbortSignal) {
    await Promise.all(docIds.map(id => this.waitForUpdated(id, signal)));
    for (const id of docIds) this.managedDatabaseDocs.add(id);
    return this.publicationStorage.readPublicationBase(docIds, signal);
  }

  commitPublication(input: LocalPublication, signal?: AbortSignal) {
    for (const member of input.members)
      this.managedDatabaseDocs.add(member.docId);
    const committed = this.publicationStorage.commitPublication(input, signal);
    for (const member of input.members) {
      let pending = this.pendingPublications.get(member.docId);
      if (!pending)
        this.pendingPublications.set(member.docId, (pending = new Set()));
      pending.add(committed);
      this.statusUpdatedSubject$.next(member.docId);
    }
    const done = () => {
      for (const member of input.members) {
        const pending = this.pendingPublications.get(member.docId);
        pending?.delete(committed);
        if (!pending?.size) this.pendingPublications.delete(member.docId);
        this.statusUpdatedSubject$.next(member.docId);
      }
    };
    void committed.then(done, done);
    return committed;
  }
  /** Publications discarded with a refused one, by operation id, with that refusal. */
  private readonly discardedPublications = new Map<
    string,
    PublicationRefusal
  >();

  /**
   * Waits for the remote end of a local publication. A refusal discards it
   * together with every journal entry of its documents, pulls their server
   * state and reports them; each discarded publication settles as refused.
   * Checked when it starts and whenever the sync state changes: a refused
   * publication isolates its documents.
   */
  async settlePublication(
    receipt: LocalPublicationReceipt,
    signal?: AbortSignal
  ): Promise<PublicationSettlement> {
    for (;;) {
      throwIfAborted(signal);
      const discarded = this.discardedPublications.get(receipt.operationId);
      if (discarded) {
        // Each receipt settles once; the entry is not needed afterwards.
        this.discardedPublications.delete(receipt.operationId);
        return { state: 'refused', refusal: discarded, docIds: [] };
      }
      const [entry] = await this.publicationStorage.listPendingPublications(
        receipt.sequence - 1,
        1
      );
      if (!entry || entry.publication.operationId !== receipt.operationId)
        return { state: 'done' };
      if (entry.state === 'BLOCKED_CONFLICT') {
        const refusal = entry.refusal ?? 'REMOTE_CONFLICT';
        const docIds = entry.publication.members.map(member => member.docId);
        return {
          state: 'refused',
          refusal,
          docIds: await this.discardRefused(docIds, refusal, signal),
        };
      }
      await lastValueFrom(
        this._state$.pipe(skip(1), first(), takeUntilAbort(signal))
      );
    }
  }

  /** Discards every publication of the documents and pulls their server state. */
  private async discardRefused(
    docIds: readonly string[],
    refusal: PublicationRefusal,
    signal?: AbortSignal
  ): Promise<readonly string[]> {
    const ids = new Set(docIds);
    for (let after = 0; ; ) {
      const page = await this.publicationStorage.listPendingPublications(
        after,
        256
      );
      for (const pending of page)
        if (pending.publication.members.some(member => ids.has(member.docId)))
          this.discardedPublications.set(
            pending.publication.operationId,
            refusal
          );
      if (page.length < 256) break;
      after = page.at(-1)!.sequence;
    }
    const affected = await this.resolveRefusedPublications(docIds, 'discard');
    for (const docId of affected) await this.pullRemote(docId, signal);
    return affected;
  }

  private get refusedPublicationStorage(): RefusedPublicationStorage {
    const storage = this.storage;
    if (!isRefusedPublicationStorage(storage))
      throw new PublicationError('FORMAT_MISMATCH');
    return storage;
  }
  listRefusedPublications(docIds: readonly string[]) {
    return this.refusedPublicationStorage.listRefusedPublications(docIds);
  }
  /**
   * retry: the affected documents leave their isolation and sync now.
   * discard: their open copies are detached; the caller reloads them, the
   * next pull restores the server state.
   */
  async resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ): Promise<readonly string[]> {
    const affected =
      await this.refusedPublicationStorage.resolveRefusedPublications(
        docIds,
        resolution
      );
    for (const docId of affected) {
      if (resolution === 'discard') {
        this.forgetRefusedSaves(docId);
        const doc = this.status.docs.get(docId);
        if (doc) this.disconnectDoc(doc);
        this.managedDatabaseDocs.delete(docId);
      } else await this.sync.repairDoc(docId);
    }
    return affected;
  }

  private readonly uniqueId = `frontend:${nanoid()}`;

  private readonly prioritySettings = new Map<string, number>();

  private readonly status = {
    docs: new Map<string, YDoc>(),
    connectedDocs: new Set<string>(),
    readyDocs: new Set<string>(),
    jobDocQueue: new AsyncPriorityQueue(),
    jobMap: new Map<string, Job[]>(),
    currentJob: null as { docId: string; jobs: Job[] } | null,
  };

  private readonly statusUpdatedSubject$ = new Subject<string>();

  // Retained independently of connected YDocs: closing a page must not drop
  // writes the local store refused. Each run keeps its action provenance.
  private readonly refusedSaves = new Map<string, (Job & { type: 'save' })[]>();
  private readonly localQuotaExceededSubject$ = new Subject<string>();
  readonly localQuotaExceeded$ = this.localQuotaExceededSubject$.asObservable();

  /** Called when the existing device-storage recheck reports room again. */
  retryRefusedSaves() {
    for (const docId of this.refusedSaves.keys()) {
      this.status.jobDocQueue.push(
        docId,
        this.prioritySettings.get(docId) ?? 0
      );
    }
  }

  private forgetRefusedSaves(docId: string) {
    this.refusedSaves.delete(docId);
    this.statusUpdatedSubject$.next(docId);
  }

  // Failed loads of open pages are tried again, waiting longer each time;
  // one pending try per document.
  private readonly loadRetries = new Map<
    string,
    { attempt: number; timer: ReturnType<typeof setTimeout> | null }
  >();

  private retryLoadLater(docId: string, signal?: AbortSignal) {
    const retry = this.loadRetries.get(docId) ?? { attempt: 0, timer: null };
    if (retry.timer) return;
    retry.attempt += 1;
    retry.timer = setTimeout(
      () => {
        retry.timer = null;
        if (this.status.docs.has(docId) && this.status.jobMap.has(docId))
          this.status.jobDocQueue.push(
            docId,
            this.prioritySettings.get(docId) ?? 0
          );
      },
      Math.min(LOAD_RETRY_BASE_MS * 2 ** (retry.attempt - 1), LOAD_RETRY_MAX_MS)
    );
    this.loadRetries.set(docId, retry);
    if (retry.attempt === 1)
      signal?.addEventListener('abort', () => this.forgetLoadRetry(docId), {
        once: true,
      });
  }

  private forgetLoadRetry(docId: string) {
    const timer = this.loadRetries.get(docId)?.timer;
    if (timer) clearTimeout(timer);
    this.loadRetries.delete(docId);
  }

  private readonly abort = new AbortController();

  constructor(
    public readonly storage: DocStorage,
    private readonly sync: DocSync,
    private _options: DocFrontendOptions = {}
  ) {}

  /**
   * Options accessor
   */
  get options(): DocFrontendOptions {
    return this._options;
  }

  /**
   * Set callback for corruption detection.
   * Can be called after construction to wire up monitoring services.
   */
  setOnCorruptionDetected(
    callback: DocFrontendOptions['onCorruptionDetected']
  ): void {
    this._options = { ...this._options, onCorruptionDetected: callback };
  }

  private _docState$(docId: string): Observable<DocFrontendDocState> {
    const frontendState$ = new Observable<{
      ready: boolean;
      loaded: boolean;
      updating: boolean;
    }>(subscribe => {
      const next = () => {
        subscribe.next({
          ready: this.status.readyDocs.has(docId),
          loaded: this.status.connectedDocs.has(docId),
          updating:
            (this.pendingPublications.get(docId)?.size ?? 0) > 0 ||
            this.refusedSaves.has(docId) ||
            (this.status.jobMap.get(docId)?.length ?? 0) > 0 ||
            this.status.currentJob?.docId === docId,
        });
      };
      next();
      return this.statusUpdatedSubject$.subscribe(updatedId => {
        if (updatedId === docId) next();
      });
    });
    const syncState$ = this.sync.docState$(docId);
    return combineLatest([frontendState$, syncState$]).pipe(
      map(([frontend, sync]) => ({
        ...frontend,
        synced: sync.synced,
        syncing: sync.syncing,
        syncRetrying: sync.retrying,
        syncErrorMessage: sync.errorMessage,
        syncIsolatedFailure: sync.isolatedFailure ?? null,
        syncRemoteAhead: sync.remoteAhead,
      }))
    );
  }

  docState$(docId: string): Observable<DocFrontendDocState> {
    return this._docState$(docId).pipe(
      throttleTime(1000, undefined, {
        trailing: true,
        leading: true,
      })
    );
  }

  private readonly _state$ = combineLatest([
    new Observable<{ total: number; loaded: number; updating: boolean }>(
      subscriber => {
        const next = () => {
          subscriber.next({
            total: this.status.docs.size,
            loaded: this.status.connectedDocs.size,
            updating:
              this.status.jobMap.size > 0 ||
              this.refusedSaves.size > 0 ||
              this.status.currentJob !== null ||
              this.pendingPublications.size > 0,
          });
        };
        next();
        return this.statusUpdatedSubject$.subscribe(() => {
          next();
        });
      }
    ),
    this.sync.state$,
  ]).pipe(
    map(([frontend, sync]) => ({
      total: sync.total ?? frontend.total,
      loaded: frontend.loaded,
      updating: frontend.updating,
      syncing: sync.syncing,
      synced: sync.synced,
      syncRetrying: sync.retrying,
      syncErrorMessage: sync.errorMessage,
      syncIsolatedFailures: sync.isolatedFailures ?? [],
    })),
    share({
      connector: () => new ReplaySubject(1),
    })
  ) satisfies Observable<DocFrontendState>;

  state$ = this._state$.pipe(
    throttleTime(1000, undefined, {
      leading: true,
      trailing: true,
    })
  );

  start() {
    if (this.abort.signal.aborted) {
      throw new Error('doc frontend can only start once');
    }
    this.mainLoop(this.abort.signal).catch(error => {
      if (error !== MANUALLY_STOP) {
        console.error(error);
      }
    });
  }

  stop() {
    this.abort.abort(MANUALLY_STOP);
  }

  private async mainLoop(signal?: AbortSignal) {
    await this.storage.connection.waitForConnected(signal);
    const dispose = this.storage.subscribeDocUpdate((record, origin) => {
      this.event.onStorageUpdate(record, origin);
    });
    try {
      // wait for storage to connect
      await Promise.race([
        this.storage.connection.waitForConnected(signal),
        new Promise((_, reject) => {
          signal?.addEventListener('abort', reason => {
            reject(reason);
          });
        }),
      ]);

      while (true) {
        throwIfAborted(signal);

        const docId = await this.status.jobDocQueue.asyncPop(undefined, signal);
        const jobs = this.status.jobMap.get(docId) ?? [];
        this.status.jobMap.delete(docId);

        if (jobs.length === 0 && !this.refusedSaves.has(docId)) {
          this.statusUpdatedSubject$.next(docId);
          continue;
        }

        this.status.currentJob = { docId, jobs };
        this.statusUpdatedSubject$.next(docId);

        const { apply, load, save } = groupBy(jobs, job => job.type) as {
          [key in Job['type']]?: Job[];
        };

        let saveStarted = false;
        try {
          if (load?.length) {
            await this.jobs.load(load[0] as any, signal);
            this.forgetLoadRetry(docId);
          }

          for (const applyJob of apply ?? []) {
            await this.jobs.apply(applyJob as any, signal);
          }

          if (save?.length || this.refusedSaves.has(docId)) {
            saveStarted = true;
            await this.jobs.save(
              docId,
              (save ?? []) as (Job & { type: 'save' })[],
              signal
            );
          }
        } catch (error) {
          if (signal?.aborted) throw error;
          // A failed job stops only its document, the other documents go on.
          // Its writes are kept as retained saves: a failed save retains them
          // itself, a failure before the save retains the batch's saves. They
          // survive closing the page, the next save of the document or a
          // recovery from a full device writes them, and until then closing
          // the tab warns. An open page keeps the batch's load and applies; a
          // failed load is tried again with growing waits, a closed page loads
          // anew when it is opened.
          if (!saveStarted) {
            if (save?.length)
              this.refusedSaves.set(docId, [
                ...(this.refusedSaves.get(docId) ?? []),
                ...(save as (Job & { type: 'save' })[]),
              ]);
            const unrun = jobs.filter(job => job.type !== 'save');
            if (unrun.length && this.status.docs.has(docId)) {
              this.status.jobMap.set(docId, [
                ...unrun,
                ...(this.status.jobMap.get(docId) ?? []),
              ]);
              if (load?.length) this.retryLoadLater(docId, signal);
            }
          }
          console.error(error);
        }

        this.status.currentJob = null;
        this.statusUpdatedSubject$.next(docId);
      }
    } finally {
      dispose();
    }
  }

  /**
   * Connect a doc to the frontend, the doc will sync with the doc storage.
   * @param doc - The doc to connect
   */
  connectDoc(doc: YDoc) {
    this._connectDoc(doc);
  }

  async deleteDoc(docId: string) {
    // Forget first: a retry while the delete runs must not write the doc again.
    this.forgetRefusedSaves(docId);
    await this.sync.removeDoc(docId);
    await this.storage.deleteDoc(docId);
  }

  readonly jobs = {
    load: async (job: Job & { type: 'load' }, signal?: AbortSignal) => {
      const doc = this.status.docs.get(job.docId);
      if (!doc) {
        return;
      }

      // GUID invariant check - critical for data integrity
      // If this fails, data could be saved under wrong docId causing corruption
      if (doc.guid !== job.docId) {
        const error = new Error(
          `[DocFrontend] GUID mismatch detected! job.docId=${job.docId}, doc.guid=${doc.guid}. ` +
            `This indicates a serious bug that could cause data corruption.`
        );
        console.error(error.message);
        throw error;
      }

      const docRecord = await this.storage.getDoc(job.docId);
      throwIfAborted(signal);

      const storageStateVector =
        docRecord && !isEmptyUpdate(docRecord.bin)
          ? encodeStateVectorFromUpdate(docRecord.bin)
          : undefined;

      if (docRecord && !isEmptyUpdate(docRecord.bin)) {
        this.applyUpdate(job.docId, docRecord.bin);

        this.status.readyDocs.add(job.docId);
      }

      const localDiff = storageStateVector
        ? encodeStateAsUpdate(doc, storageStateVector)
        : encodeStateAsUpdate(doc);

      // Equal states still produce a diff carrying the whole delete set.
      const localDiffChangesStorage =
        docRecord && storageStateVector
          ? remoteDiffChangesLocal(docRecord.bin, storageStateVector, localDiff)
          : !isEmptyUpdate(localDiff);

      // Restore retained edits after computing the storage diff so they keep
      // their original provenance when retried, rather than becoming new edits.
      for (const pending of this.refusedSaves.get(job.docId) ?? []) {
        this.applyUpdate(job.docId, pending.update);
        this.status.readyDocs.add(job.docId);
      }

      const drainedPendingApplies = this.drainPendingApplies(doc);
      if (drainedPendingApplies > 0) {
        this.status.readyDocs.add(job.docId);
      }

      this.status.connectedDocs.add(job.docId);
      doc.emit('sync', [true, doc]);
      if (localDiffChangesStorage && !this.managedDatabaseDocs.has(job.docId)) {
        // Persist the local-only state inside the load job. A scheduled save
        // would run after the saves already batched with this load, so later
        // deltas reached storage and the sync peer before the causally earlier
        // creation state (including its initialization placement metadata).
        await this.jobs.save(
          job.docId,
          [{ type: 'save', docId: job.docId, update: localDiff }],
          signal
        );
      }
      this.statusUpdatedSubject$.next(job.docId);
    },
    save: async (
      docId: string,
      jobs: (Job & { type: 'save' })[],
      signal?: AbortSignal
    ) => {
      if (!this.status.docs.has(docId) && !this.refusedSaves.has(docId)) return;
      if (this.managedDatabaseDocs.has(docId))
        throw new PublicationError('FORMAT_MISMATCH');
      const pending = [...(this.refusedSaves.get(docId) ?? []), ...jobs];
      if (pending.length === 0) return;
      this.refusedSaves.set(docId, pending);
      for (const run of provenanceRuns(pending)) {
        // Keep the run retained through merge, cancellation and storage errors.
        throwIfAborted(signal);
        const merged = await this.mergeUpdates(run.updates);
        throwIfAborted(signal);
        // Explicit delete/discard may have removed the pending writes meanwhile.
        if (this.refusedSaves.get(docId) !== pending) return;
        try {
          if (!isEmptyUpdate(merged)) {
            await this.storage.pushDocUpdate(
              {
                docId,
                bin: merged,
                ...(run.clientAction ? { clientAction: run.clientAction } : {}),
              },
              this.uniqueId
            );
          }
        } catch (error) {
          if (this.refusedSaves.get(docId) === pending) {
            // Compact the failed run without losing the following provenance runs.
            pending.splice(0, run.updates.length, {
              type: 'save',
              docId,
              update: merged,
              ...(run.clientAction ? { clientAction: run.clientAction } : {}),
            });
            this.statusUpdatedSubject$.next(docId);
          }
          // Worker errors retain their code, not necessarily their prototype.
          if (
            !error ||
            typeof error !== 'object' ||
            !('code' in error) ||
            error.code !== 'LOCAL_QUOTA'
          )
            throw error;
          this.localQuotaExceededSubject$.next(docId);
          return;
        }
        if (this.refusedSaves.get(docId) !== pending) return;
        pending.splice(0, run.updates.length);
        if (pending.length === 0) this.forgetRefusedSaves(docId);
      }
    },
    apply: async (job: Job & { type: 'apply' }, signal?: AbortSignal) => {
      throwIfAborted(signal);
      if (!this.status.docs.has(job.docId)) {
        return;
      }
      if (!this.status.connectedDocs.has(job.docId)) {
        // A doc nobody has open yet loads its pending bytes like stored state.
        this.queuePendingApply(job.docId, job.update);
      } else {
        this.applyUpdate(job.docId, job.update, job.agent);
      }
      if (!isEmptyUpdate(job.update)) {
        this.status.readyDocs.add(job.docId);
        this.statusUpdatedSubject$.next(job.docId);
      }
    },
  };

  event = {
    onStorageUpdate: (update: DocRecord, origin?: string) => {
      if (origin !== this.uniqueId) {
        this.schedule({
          type: 'apply',
          docId: update.docId,
          update: update.bin,
          ...(update.agent ? { agent: update.agent } : {}),
        });
      }
    },
  };

  /**
   * Disconnect a doc from the frontend, the doc will stop syncing with the doc storage.
   * It's not recommended to use this method directly, better to use `doc.destroy()`.
   *
   * @param doc - The doc to disconnect
   */
  disconnectDoc(doc: YDoc) {
    this.status.docs.delete(doc.guid);
    this.status.connectedDocs.delete(doc.guid);
    this.status.readyDocs.delete(doc.guid);
    this.forgetLoadRetry(doc.guid);
    if (!this.refusedSaves.has(doc.guid))
      this.status.jobDocQueue.remove(doc.guid);
    this.status.jobMap.delete(doc.guid);
    this.statusUpdatedSubject$.next(doc.guid);
    doc.off('update', this.handleDocUpdate);
  }

  /**
   * sl-suoi.1.3: Dynamically add a doc to the sync queue.
   * Used for on-demand doc loading (navigation, folder expand, warm sync).
   * Idempotent — calling with an already-syncing docId is a no-op.
   */
  addDoc(docId: string, source: DocSyncRequestSource) {
    this.sync?.addDoc(docId, source);
  }

  /** The pages this device holds for local search, in order, with a budget. */
  setWarmCache(selection: WarmCacheSelection) {
    this.sync.setWarmCache(selection);
  }

  /** The device ran out of space for the warm cache: its budget halves. */
  shrinkWarmCache() {
    this.sync.shrinkWarmCache();
  }

  /** The pages the warm cache holds; `all` for every stored page. */
  get warmCacheHeld$() {
    return this.sync.warmCacheHeld$;
  }

  /** What the pages the warm cache holds take, and their blobs. */
  get warmCacheContents$() {
    return this.sync.warmCacheContents$;
  }

  debugDocState(docId: string) {
    return this.sync?.debugDocState(docId) ?? Promise.resolve([]);
  }

  async releaseDoc(docId: string, source: DocSyncRequestSource) {
    await this.sync?.releaseDoc(docId, source);
  }

  addPriority(id: string, priority: number) {
    const undoSyncPriority = this.sync?.addPriority(id, priority);
    const undoFrontendPriority = addCumulativePriority(
      this.prioritySettings,
      id,
      priority,
      (docId, nextPriority) =>
        this.status.jobDocQueue.setPriority(docId, nextPriority)
    );

    return () => {
      undoFrontendPriority();
      undoSyncPriority?.();
    };
  }

  private _connectDoc(doc: YDoc) {
    const registeredDoc = this.status.docs.get(doc.guid);
    if (registeredDoc === doc) {
      return;
    }
    if (registeredDoc) {
      throw new Error('doc already connected');
    }

    this.schedule({
      type: 'load',
      docId: doc.guid,
    });

    this.status.docs.set(doc.guid, doc);
    this.statusUpdatedSubject$.next(doc.guid);

    doc.on('update', this.handleDocUpdate);

    doc.on('destroy', () => {
      this.disconnectDoc(doc);
    });

    // Pending storage applies are drained by the load job after it computes the
    // local-only diff. Draining here would make remote bytes look like local
    // offline edits and echo them back to storage.
  }

  private schedule(job: Job) {
    const priority = this.prioritySettings.get(job.docId) ?? 0;
    this.status.jobDocQueue.push(job.docId, priority);

    const existingJobs = this.status.jobMap.get(job.docId) ?? [];
    existingJobs.push(job);
    this.status.jobMap.set(job.docId, existingJobs);
    this.statusUpdatedSubject$.next(job.docId);
  }

  private isApplyingUpdate = false;

  /** Origins this frontend applied agent updates with; never written back. */
  private readonly agentOrigins = new WeakSet<AgentOrigin>();

  private agentOrigin(agent: AgentDocUpdate): AgentOrigin {
    const origin = createAgentOrigin(agent);
    this.agentOrigins.add(origin);
    return origin;
  }

  /**
   * sl-497z / sl-91j4: when storage emits a snapshot for a docId before the
   * workspace has called `onLoadDoc` for that subdoc, `applyUpdate` used to
   * silently drop the bytes (status.docs.get(docId) === undefined). The
   * symptom was sharded database rows appearing empty after a remote write:
   * the shard subdoc had server-state, but the frontend Y.Doc instance had
   * been observed at zero rows and never received the update.
   *
   * Fix: buffer pending applies keyed by docId; when `connectDoc` for that
   * guid runs, drain the buffer in arrival order. We hold full update bytes
   * (not state vectors) so Y.js's commutative apply produces the same final
   * state regardless of arrival order. Buffer is unbounded by design —
   * single-producer doc-persist already caps update size at the worker.
   */
  private readonly pendingApplies = new Map<string, Uint8Array[]>();

  private queuePendingApply(docId: string, update: Uint8Array) {
    if (isEmptyUpdate(update)) return;

    const queue = this.pendingApplies.get(docId) ?? [];
    queue.push(update);
    this.pendingApplies.set(docId, queue);
  }

  private drainPendingApplies(doc: YDoc) {
    const pending = this.pendingApplies.get(doc.guid);
    if (!pending || pending.length === 0) {
      return 0;
    }
    this.pendingApplies.delete(doc.guid);
    let applied = 0;
    for (const update of pending) {
      if (isEmptyUpdate(update)) continue;
      try {
        this.isApplyingUpdate = true;
        applyUpdate(doc, update, NBSTORE_ORIGIN);
        applied += 1;
      } catch (err) {
        console.error(
          `[doc-persist] failed to drain pending apply for docId=${doc.guid}:`,
          err
        );
      } finally {
        this.isApplyingUpdate = false;
      }
    }
    return applied;
  }

  /**
   * Applies a storage update to the open doc: with the agent step's origin
   * when an agent made it, otherwise as storage.
   */
  applyUpdate(docId: string, update: Uint8Array, agent?: AgentDocUpdate) {
    const doc = this.status.docs.get(docId);
    if (!doc) {
      // sl-497z / sl-91j4: storage emitted a snapshot for a subdoc that the
      // workspace has not yet registered via onLoadDoc. Buffer the bytes —
      // they will be drained when connectDoc(doc) eventually runs.
      this.queuePendingApply(docId, update);
      return;
    }
    if (!isEmptyUpdate(update)) {
      try {
        this.isApplyingUpdate = true;
        applyUpdate(
          doc,
          update,
          agent ? this.agentOrigin(agent) : NBSTORE_ORIGIN
        );
      } catch (err) {
        console.error('failed to apply update yjs doc', err);

        // Report corruption if callback is provided
        if (this.options.onCorruptionDetected && err instanceof Error) {
          try {
            this.options.onCorruptionDetected({
              docId,
              error: err,
              binaryData: update,
              operation: 'applyUpdate',
            });
          } catch (reportErr) {
            console.error('failed to report corruption', reportErr);
          }
        }
      } finally {
        this.isApplyingUpdate = false;
      }
    }
  }

  private readonly handleDocUpdate = (
    update: Uint8Array,
    origin: any,
    doc: YDoc,
    transaction: YTransaction
  ) => {
    if (
      origin === NBSTORE_ORIGIN ||
      this.agentOrigins.has(origin) ||
      (typeof origin === 'string' && origin.startsWith('local-publication:'))
    ) {
      return;
    }
    if (this.managedDatabaseDocs.has(doc.guid))
      throw new PublicationError('FORMAT_MISMATCH');
    if (this.isApplyingUpdate && isBuildDebugEnabled()) {
      let changedList = '';
      for (const [changed, keys] of transaction.changed) {
        for (const key of keys) {
          if (changed instanceof YMap && key) {
            changedList += `${key} => ${changed.get(key)}\n`;
          }
        }
      }
      console.warn(`⚠️ When nbstore applies a remote update, some code triggers a local change to the doc.
This will causes the document's 'edited by' to become the current user, even if the user has not actually modified the document.
This is usually caused by a coding error and needs to be fixed by the developer.
Changed:
${changedList}
`);
    }
    // A change with a local origin is stored even while a remote update is
    // being applied. Dropping it leaves a gap in this client's history, and
    // every later change of the client then stays unreadable for everyone
    // else: typed text is lost after reload.
    if (!this.status.docs.has(doc.guid)) {
      return;
    }

    // Invariant check: ensure the doc sending the update is the same we registered
    const registeredDoc = this.status.docs.get(doc.guid);
    if (registeredDoc !== doc) {
      console.error(
        `[DocFrontend] Doc instance mismatch for guid=${doc.guid}. ` +
          `Update from unregistered doc instance ignored.`
      );
      return;
    }

    const provenance =
      origin?.kind === 'agent'
        ? ClientActionAgentSchema.safeParse(origin)
        : null;
    this.schedule({
      type: 'save',
      docId: doc.guid,
      update,
      ...(provenance?.success
        ? {
            clientAction: {
              ...provenance.data.clientAction,
              targets: provenance.data.targets.slice(-64),
            },
          }
        : {}),
    });
  };

  protected mergeUpdates(updates: Uint8Array[]) {
    const merge = this.options?.mergeUpdates ?? mergeUpdates;

    return merge(updates.filter(bin => !isEmptyUpdate(bin)));
  }

  async waitForUpdated(docId?: string, abort?: AbortSignal) {
    const source$: Observable<DocFrontendDocState | DocFrontendState> = docId
      ? this._docState$(docId)
      : this._state$;
    await lastValueFrom(
      source$.pipe(
        filter(status => !status.updating),
        takeUntilAbort(abort),
        first()
      )
    );
    return;
  }

  async waitForDocLoaded(docId: string, abort?: AbortSignal) {
    await lastValueFrom(
      this._docState$(docId).pipe(
        filter(state => state.loaded),
        takeUntilAbort(abort),
        first()
      )
    );
  }

  async waitForSynced(docId?: string, abort?: AbortSignal) {
    await this.waitForUpdated(docId, abort);
    await this.sync.waitForSynced(docId, abort);
  }

  /**
   * User-triggered repair of one doc: retries it now if it is isolated and
   * reconciles it with the server. Returns one result per remote peer.
   */
  async repairDoc(docId: string) {
    return this.sync.repairDoc(docId);
  }

  /** Pull an authoritative server mutation without pushing local state. */
  async pullRemote(docId: string, signal?: AbortSignal) {
    return this.sync.pullRemote(docId, signal);
  }

  /**
   * sl-sync-flush: Flush all pending updates and wait for backend ACK.
   * Use this before automation execution to ensure all local changes
   * have been synced to the backend.
   */
  async flushAndWaitForSynced(docId: string, abort?: AbortSignal) {
    await traceImportAwait('frontend.wait-for-updated', docId, () =>
      this.waitForUpdated(docId, abort)
    );
    await traceImportAwait('frontend.flush-rpc', docId, () =>
      this.sync.flushAndWaitForSynced(docId, abort)
    );
  }

  async waitForDocReady(docId: string, abort?: AbortSignal) {
    await lastValueFrom(
      this._docState$(docId).pipe(
        filter(state => state.ready),
        takeUntilAbort(abort),
        first()
      )
    );
  }

  async resetSync() {
    await this.sync.resetSync();
  }
}
